import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import {showSnackbar} from "./Snackbar";
import {elapsedCallSeconds, formatCallTime} from "./callTime";

const queueLabel = (entries) => {
  const first = entries[0]?.queueNumber;
  const last = entries[entries.length - 1]?.queueNumber;
  return first === last ? `Queue ${first}` : `Queue ${first}-${last}`;
};

const copyCallerId = async (entry) => {
  if (String(entry.id).includes("null")) {
    showSnackbar("Anonymous caller.", "warning");
    return;
  }
  try {
    await navigator.clipboard.writeText(entry.id);
    showSnackbar("Caller ID copied.");
  } catch (_) {
    showSnackbar("Caller ID could not be copied.", "warning");
  }
};

const CallerList = ({
  entries,
  now,
  compact = false,
  reviewerMonitoring,
}) => (
  <Stack
    divider={<Divider flexItem />}
    sx={{minWidth: compact ? 220 : 260, maxHeight: compact ? 220 : 320, overflowY: "auto"}}
  >
    {entries.map((entry) => (
      <Stack key={entry.id} direction="row" alignItems="center" spacing={1} sx={{py: 0.75, px: 1}}>
        <Chip size="small" label={entry.queueNumber} sx={{fontWeight: 700}} />
        <Tooltip title={String(entry.id).includes("null") ? "Anonymous caller" : "Copy caller ID"}>
          <span>
            <IconButton
              size="small"
              disabled={String(entry.id).includes("null")}
              onClick={() => copyCallerId(entry)}
              aria-label={`Copy caller ID for queue ${entry.queueNumber}`}
            >
              <PersonRoundedIcon sx={{fontSize: 18}} />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography variant="body2" color="text.primary" noWrap sx={{fontWeight: 650}}>
            {entry.answeredBy || "Agent"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {String(entry.id).includes("null") ? "Anonymous" : entry.id}
          </Typography>
          <Typography
            variant="caption"
            color="success.main"
            noWrap
            sx={{fontVariantNumeric: "tabular-nums"}}
          >
            {entry.timeAnswered ?
              `Live · ${formatCallTime(elapsedCallSeconds(entry.timeAnswered, now))}` :
              "On a call"}
          </Typography>
        </Box>
        {reviewerMonitoring?.getState(entry)?.available ||
          reviewerMonitoring?.getState(entry)?.listening ? <Tooltip
            title={reviewerMonitoring.getState(entry).listening ?
              "Stop listening" : "Listen to this call"}
          >
            <span>
              <IconButton
                size="small"
                color={reviewerMonitoring.getState(entry).listening ?
                  "primary" : "default"}
                disabled={reviewerMonitoring.getState(entry).joining}
                onClick={(event) => {
                  event.stopPropagation();
                  reviewerMonitoring.listen(entry);
                }}
                aria-label={reviewerMonitoring.getState(entry).listening ?
                  `Stop listening to ${entry.answeredBy}` :
                  `Listen to ${entry.answeredBy}'s call`}
              >
                <HeadphonesRoundedIcon sx={{fontSize: 18}} />
              </IconButton>
            </span>
          </Tooltip> :
          <PhoneInTalkRoundedIcon color="success" sx={{fontSize: 17}} />}
      </Stack>
    ))}
  </Stack>
);

export default function ActiveCallStack({
  entries,
  now,
  onExpand,
  reviewerMonitoring,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const count = entries.length;
  const title = `${count} active ${count === 1 ? "call" : "calls"}`;

  return (
    <Box
      sx={{
        mx: 0.5,
        flex: "0 0 auto",
        width: {xs: "100%", sm: 138, md: 146, lg: 154, xl: 162},
        scrollSnapAlign: "start",
      }}
    >
      <Tooltip
        arrow
        enterDelay={250}
        placement="bottom"
        slotProps={{
          tooltip: {
            sx: (theme) => ({
              width: 320,
              maxWidth: "min(320px, calc(100vw - 32px))",
              p: 0.8,
              color: "text.primary",
              bgcolor: theme.palette.mode === "dark" ? "#1b2522" : "#ffffff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "10px",
              boxShadow: theme.palette.mode === "dark" ?
                "0 18px 42px rgba(0,0,0,0.34)" :
                "0 18px 42px rgba(20,48,40,0.14)",
            }),
          },
          arrow: {
            sx: (theme) => ({
              color: theme.palette.mode === "dark" ? "#1b2522" : "#ffffff",
              "&::before": {border: "1px solid", borderColor: "divider"},
            }),
          },
        }}
        title={(
          <Box sx={{py: 0.4}}>
            <Typography variant="caption" color="text.primary" sx={{px: 1, fontWeight: 700}}>
              Calls handled by other agents
            </Typography>
            <CallerList
              entries={entries}
              now={now}
              compact
              reviewerMonitoring={reviewerMonitoring}
            />
          </Box>
        )}
      >
        <ButtonBase
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label={`${title}, ${queueLabel(entries)}. View details.`}
          sx={{width: "100%", borderRadius: "10px", textAlign: "inherit"}}
        >
          <Box sx={{position: "relative", width: "100%", height: {xs: 270, sm: 300}}}>
            {[2, 1].map((layer) => (
              <Paper
                key={layer}
                elevation={0}
                sx={{
                  position: "absolute",
                  inset: `${layer * 7}px ${layer * 3}px ${layer * 3}px ${layer * 7}px`,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  bgcolor: "background.paper",
                  opacity: 0.48 + (layer * 0.12),
                }}
              />
            ))}
            <Paper
              elevation={0}
              sx={{
                position: "absolute",
                inset: "0 10px 10px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: (theme) => theme.palette.mode === "dark" ? "#1a2522" : "#f7faf9",
                transition: "border-color 160ms ease, background-color 160ms ease",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: (theme) => theme.palette.mode === "dark" ? "#20302c" : "#eef7f3",
                },
              }}
            >
              <Stack alignItems="center" spacing={1.1} sx={{px: 1.2}}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "action.hover",
                    color: "primary.main",
                  }}
                >
                  <GroupsRoundedIcon sx={{fontSize: 29}} />
                </Box>
                <Typography variant="h5" sx={{fontWeight: 720, letterSpacing: -0.5}}>
                  {count}
                </Typography>
                <Typography variant="subtitle2" align="center" sx={{fontWeight: 650, lineHeight: 1.15}}>
                  Active {count === 1 ? "call" : "calls"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {queueLabel(entries)}
                </Typography>
                <Typography variant="caption" color="primary.main" sx={{fontWeight: 650}}>
                  View callers
                </Typography>
              </Stack>
            </Paper>
          </Box>
        </ButtonBase>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
        slotProps={{paper: {sx: {mt: 0.7, borderRadius: "10px"}}}}
      >
        <Box sx={{p: 1}}>
          <Typography variant="subtitle2" sx={{px: 1, pt: 0.5, pb: 0.8, fontWeight: 700}}>
            {title} · {queueLabel(entries)}
          </Typography>
          <CallerList
            entries={entries}
            now={now}
            reviewerMonitoring={reviewerMonitoring}
          />
          <Button
            fullWidth
            variant="contained"
            startIcon={<UnfoldMoreRoundedIcon />}
            onClick={() => {
              setAnchorEl(null);
              onExpand?.();
            }}
            sx={{mt: 1, borderRadius: "8px"}}
          >
            Expand {count} {count === 1 ? "call" : "calls"}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
